import dayjs from 'dayjs';
import type * as Types from '@/graphql/types';
import { addDays, today } from '@/helpers/date-helpers';
import { GroupDataSchema, RmRecsDataSchema } from '../schemas';

/**
 * Formats a date string for notes display
 * e.g., "2025-01-15" -> "Wed, 1/15" (same year) or "Wed, 1/15/2026" (different year)
 */
function formatDateForNotes(dateString: string): string {
  const date = dayjs(dateString);
  const dayAbbr = date.format('ddd'); // Mon, Tue, etc.
  const currentYear = dayjs().year();
  const dateYear = date.year();

  if (dateYear !== currentYear) {
    return `${dayAbbr}, ${date.format('M/D/YYYY')}`;
  }
  return `${dayAbbr}, ${date.format('M/D')}`;
}

export interface ProcessedRmRecsData {
  hasOpenRecommendations: boolean;
  formattedSummary: string;
  openCount: number;
}

export interface ProcessedGroupData {
  hasGroupsWithRooms: boolean;
  formattedSummaryItems: string[];
}

export interface ProcessedNewGroupsData {
  hasNewGroups: boolean;
  formattedSummaryItems: string[];
}

/**
 * Enhanced RM Recs transformer that pre-calculates summary text
 * Removes text generation logic from LLM prompts
 */
export const transformRmRecsData = (rmRecsData?: any): ProcessedRmRecsData => {
  if (!rmRecsData || !rmRecsData.hotelRmRecs) {
    return {
      hasOpenRecommendations: false,
      formattedSummary: '',
      openCount: 0,
    };
  }

  const validItems = rmRecsData.hotelRmRecs
    .map((item: any) => {
      const result = RmRecsDataSchema.safeParse(item);
      return result.success ? result.data : null;
    })
    .filter((item: any) => item !== null)
    .filter((item: any) => item.status === 'OPEN');

  const openCount = validItems.length;

  if (openCount === 0) {
    return {
      hasOpenRecommendations: false,
      formattedSummary: '',
      openCount: 0,
    };
  }

  // Pre-calculate the summary text
  const isPlural = openCount > 1;
  const verb = isPlural ? 'are' : 'is';
  const noun = isPlural ? 'recommendations' : 'recommendation';

  // Check if we need month information
  const months = new Set(validItems.map((item: any) => dayjs(item.stay_date).format('MMMM YYYY')));

  const sixtyDaysOut = dayjs().add(60, 'day');
  const allWithinSixtyDays = validItems.every((item: any) =>
    dayjs(item.stay_date).isBefore(sixtyDaysOut),
  );

  let summary: string;
  if (allWithinSixtyDays) {
    summary = `There ${verb} ${openCount} rate ${noun} awaiting approval.`;
  } else {
    const monthList = Array.from(months).sort();
    if (monthList.length === 1) {
      summary = `There ${verb} ${openCount} rate ${noun} awaiting approval for ${monthList[0]}.`;
    } else {
      const lastMonth = monthList.pop();
      summary = `There ${verb} ${openCount} rate ${noun} awaiting approval for ${monthList.join(', ')} and ${lastMonth}.`;
    }
  }

  return {
    hasOpenRecommendations: true,
    formattedSummary: summary,
    openCount,
  };
};

/**
 * Enhanced Group data transformer that pre-calculates summary items
 * Removes formatting logic from LLM prompts
 */
export const transformGroupData = (groupData: Types.Query['groupDaily']): ProcessedGroupData => {
  if (!groupData) {
    return {
      hasGroupsWithRooms: false,
      formattedSummaryItems: [],
    };
  }

  const validItems = groupData
    .map((item) => {
      const result = GroupDataSchema.safeParse(item);
      return result.success ? result.data : null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .map((item) => {
      // Only keep details where snapshot_date is today or in the future (UTC)
      const todayStart = dayjs.utc(today()).startOf('day');
      const transformedDetails = (item.details || [])
        .map((detail) => {
          // Handle both timestamp numbers and date strings
          let parsedDate;
          if (detail.snapshot_date) {
            const asNumber = Number(detail.snapshot_date);
            if (!Number.isNaN(asNumber)) {
              // It's a valid number (timestamp)
              parsedDate = dayjs.utc(asNumber);
            } else {
              // It's a date string
              parsedDate = dayjs.utc(detail.snapshot_date);
            }
          }
          return {
            ...detail,
            _parsed_snapshot_date: parsedDate,
          };
        })
        .filter((detail) => {
          if (!detail._parsed_snapshot_date) return false;
          const detailDay = detail._parsed_snapshot_date.startOf('day');
          return detailDay.isSame(todayStart) || detailDay.isAfter(todayStart);
        })
        .map(({ _parsed_snapshot_date, ...rest }) => rest); // remove temp field
      return { ...item, details: transformedDetails };
    })
    .filter((item) => item.details && item.details.length > 0);

  const tenDaysOut = addDays(today(), 10);
  const filteredGroups = validItems.filter((item) => {
    return !dayjs(item.stay_date).isAfter(tenDaysOut);
  });

  // Filter out details with no rooms remaining and pre-format summary items
  // Group by rate_code and stay_date to prevent mixing different groups
  const allGroupDetails: Array<{
    stayDate: string;
    rateCode: string;
    description: string;
    remainingRooms: number;
    snapshotDate: string | number;
  }> = [];

  // Extract all individual group details with rooms remaining
  filteredGroups.forEach((group) => {
    const roomDetails = group.details?.filter((detail) => detail.group_rooms_remaining > 0) || [];

    roomDetails.forEach((detail) => {
      // Only include details with proper snapshot dates >= today
      if (!detail.snapshot_date) return;

      const snapshotDay = dayjs
        .utc(Number(detail.snapshot_date) || detail.snapshot_date)
        .startOf('day');
      const todayStart = dayjs.utc().startOf('day');
      if (!snapshotDay.isSame(todayStart) && !snapshotDay.isAfter(todayStart)) return;

      allGroupDetails.push({
        stayDate: group.stay_date!,
        rateCode: detail.rate_code!,
        description: detail.description!,
        remainingRooms: detail.group_rooms_remaining,
        snapshotDate: detail.snapshot_date,
      });
    });
  });

  // Group by rate_code (unique group identifier) to consolidate multiple dates per group
  const groupedByRateCode = allGroupDetails.reduce(
    (acc, detail) => {
      const key = detail.rateCode;
      if (!acc[key]) {
        acc[key] = {
          rateCode: detail.rateCode,
          description: detail.description,
          details: [],
        };
      }
      acc[key].details.push(detail);
      return acc;
    },
    {} as Record<
      string,
      {
        rateCode: string;
        description: string;
        details: typeof allGroupDetails;
      }
    >,
  );

  // Create summary items for each unique group, consolidating all dates
  const summaryItems = Object.values(groupedByRateCode).map((groupInfo) => {
    const { description, details } = groupInfo;

    // Sort details by stay date to ensure chronological order
    const sortedDetails = details.sort((a, b) =>
      dayjs(a.stayDate).isBefore(dayjs(b.stayDate)) ? -1 : 1,
    );

    // Create room summaries for all dates of this group
    const roomSummaries = sortedDetails.map((detail) => {
      const availabilityDate = dayjs(detail.stayDate).format('M/D');
      return `${detail.remainingRooms} rooms remaining ${availabilityDate}`;
    });

    return `${description}: ${roomSummaries.join(', ')}`;
  });

  const result = {
    hasGroupsWithRooms: summaryItems.length > 0,
    formattedSummaryItems: summaryItems,
  };

  return result;
};

/**
 * Transform and filter newly added group blocks.
 * Identifies groups that were first added yesterday (or since Friday for Monday meetings)
 * by comparing snapshots.
 *
 * @param groupData - Raw group data from GraphQL
 * @param meetingDate - The meeting date (YYYY-MM-DD)
 * @param newGroupKeys - Array of group keys (resort_id_stay_date_rate_code) that are new
 * @returns Processed new groups data with formatted summary items
 */
export const transformNewGroupsData = (
  groupData: Types.Query['groupDaily'],
  meetingDate?: string,
  newGroupKeys?: string[],
): ProcessedNewGroupsData => {
  if (!groupData || !newGroupKeys || newGroupKeys.length === 0) {
    return {
      hasNewGroups: false,
      formattedSummaryItems: [],
    };
  }

  const newGroupKeysSet = new Set(newGroupKeys);
  const meeting = dayjs(meetingDate || today());

  // Calculate 48-week cutoff (336 days)
  const maxStayDate = meeting.add(336, 'days').format('YYYY-MM-DD');

  // Extract new group details
  const newGroupDetails: Array<{
    stayDate: string;
    description: string;
    roomsCommitted: number;
    lowestRate?: number;
  }> = [];

  // Need to access brand_code from the raw groupData since GroupDataSchema doesn't include it
  groupData
    .filter((item) => item?.stay_date && item.stay_date <= maxStayDate)
    .forEach((group) => {
      if (!group || !group.brand_code || !group.stay_date || !group.details) return;

      group.details.forEach((detail) => {
        if (!detail || !detail.rate_code || !detail.description || !detail.group_rooms_committed)
          return;

        // Build the group key matching the format from findNewGroups()
        const groupKey = `${group.brand_code}_${group.stay_date}_${detail.rate_code}`;

        // Check if this group is in the "new groups" set
        const isNew = newGroupKeysSet.has(groupKey);

        if (isNew && detail.group_rooms_committed > 0 && group.stay_date) {
          newGroupDetails.push({
            stayDate: group.stay_date,
            description: detail.description,
            roomsCommitted: detail.group_rooms_committed,
            lowestRate:
              detail.lowest_group_rate && detail.lowest_group_rate > 0
                ? detail.lowest_group_rate
                : undefined,
          });
        }
      });
    });

  if (newGroupDetails.length === 0) {
    return {
      hasNewGroups: false,
      formattedSummaryItems: [],
    };
  }

  // Sort and format
  const sortedDetails = newGroupDetails.sort((a, b) => a.stayDate.localeCompare(b.stayDate));

  const summaryItems = sortedDetails.map((detail) => {
    const formattedDate = formatDateForNotes(detail.stayDate);
    const rateInfo =
      detail.lowestRate && typeof detail.lowestRate === 'number'
        ? ` at $${detail.lowestRate.toFixed(0)}`
        : '';
    return `${formattedDate}: ${detail.description} - ${detail.roomsCommitted} rooms committed${rateInfo}`;
  });

  return {
    hasNewGroups: true,
    formattedSummaryItems: summaryItems,
  };
};
